import { Fragment, useEffect, useMemo, useState } from "react";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ChevronLeft, ChevronRight, Wifi } from "lucide-react";

type Slot = { start_at: string; end_at: string };
type Studio = { id: string; name: string };

const startOfWeek = (d: Date) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday start
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};

const HOURS = Array.from({ length: 18 }, (_, i) => i + 6); // 6am – 11pm

const AdminAvailability = () => {
  const [studios, setStudios] = useState<Studio[]>([]);
  const [studioId, setStudioId] = useState<string>("");
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(new Date()));
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [live, setLive] = useState(false);

  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  useEffect(() => {
    supabase.from("studios").select("id,name").eq("active", true).order("display_order")
      .then(({ data }) => {
        setStudios((data ?? []) as Studio[]);
        if (data?.length && !studioId) setStudioId(data[0].id);
      });
  }, []);

  const load = async () => {
    if (!studioId) return;
    setLoading(true);
    const from = weekStart.toISOString();
    const toDate = new Date(weekStart); toDate.setDate(toDate.getDate() + 7);
    const { data } = await supabase.rpc("get_booked_slots", { _studio_id: studioId, _from: from, _to: toDate.toISOString() });
    setSlots(((data ?? []) as any[]).map((r: any) => ({ start_at: r.start_at, end_at: r.end_at })));
    setLoading(false);
  };

  useEffect(() => { load(); }, [studioId, weekStart]);

  // bookings are no longer broadcast via realtime (privacy); poll instead.
  useEffect(() => {
    if (!studioId) return;
    setLive(true);
    const ch = supabase.channel("availability_live")
      .on("postgres_changes", { event: "*", schema: "public", table: "calendar_blocks" }, () => load())
      .subscribe();
    const id = window.setInterval(() => { load(); }, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      setLive(false); supabase.removeChannel(ch);
      window.clearInterval(id); window.removeEventListener("focus", onFocus);
    };
  }, [studioId]);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + i); return d;
  });

  const slotCovers = (day: Date, hour: number) => {
    const cellStart = new Date(day); cellStart.setHours(hour, 0, 0, 0);
    const cellEnd = new Date(day); cellEnd.setHours(hour + 1, 0, 0, 0);
    return slots.some((s) => {
      const ss = new Date(s.start_at);
      const se = new Date(s.end_at);
      return ss < cellEnd && se > cellStart;
    });
  };

  return (
    <AdminLayout title="AVAILABILITY">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Select value={studioId} onValueChange={setStudioId}>
          <SelectTrigger className="w-64"><SelectValue placeholder="Studio…" /></SelectTrigger>
          <SelectContent>
            {studios.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-mono text-xs uppercase tracking-widest">
          {weekStart.toLocaleDateString()} – {new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString()}
        </span>
        <Button variant="outline" size="sm" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }}>
          <ChevronRight className="w-4 h-4" />
        </Button>
        <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>Today</Button>
        <span className={`ml-auto flex items-center gap-2 text-xs font-mono ${live ? "text-primary" : "text-muted-foreground"}`}>
          <Wifi className="w-3 h-3" /> {live ? "LIVE" : "—"} · {tz}
        </span>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="bg-panel border border-border overflow-x-auto">
          <div className="grid grid-cols-[60px_repeat(7,minmax(90px,1fr))] min-w-[700px]">
            <div className="border-b border-r border-border p-2 bg-background"></div>
            {days.map((d) => (
              <div key={d.toISOString()} className="border-b border-r border-border p-2 text-center bg-background">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div className="font-display text-sm">{d.getDate()}</div>
              </div>
            ))}
            {HOURS.map((h) => (
              <Fragment key={`row-${h}`}>
                <div key={`h-${h}`} className="border-b border-r border-border p-1 text-[10px] font-mono text-muted-foreground text-right pr-2">
                  {h.toString().padStart(2, "0")}:00
                </div>
                {days.map((d) => {
                  const busy = slotCovers(d, h);
                  return (
                    <div
                      key={`${d.toISOString()}-${h}`}
                      className={`border-b border-r border-border h-10 ${busy ? "bg-primary/30" : ""}`}
                      title={busy ? `Booked / blocked` : "Free"}
                    />
                  );
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs font-mono text-muted-foreground mt-4">
        Yellow cells indicate confirmed/pending bookings or calendar blocks. Updates instantly when a Stripe payment or refund changes a booking's status.
      </p>
    </AdminLayout>
  );
};

export default AdminAvailability;